/* eslint-disable react/jsx-props-no-spreading */
import { QueryClient, QueryClientProvider, keepPreviousData } from '@tanstack/react-query';
import { type FC, useCallback, useContext, useEffect, useState } from 'react';
import { useArticleListingQuery, type ArticleListingQuery } from 'cms/cms';
import CardArticle from '3-components/Cards/CardArticle/CardArticle';
import { resolveArticleListing } from 'cms/resolvers/articleListingResolver';
import ArticleListingProvider from './ArticleListingProvider';
import ArticleListingContext, { type ArticleListingContextProps, type ArticleListingProps } from './ArticleListingContext';
import { getQueryURL, getFilterQuery, getWhereValue, type WhereItem, getPageQueryURL } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import { type ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';


const ArticleListing: FC = () => {
    const {
        locale,
        activeFilters,
        setActiveFilters,
        listing: {
            filters,
            itemsPerPage = 10,
            filterByAncestors,
        },
        page,
        setPage,
        label,
        cms,
        serverData,
        serverPageUrl,
    } = useContext(ArticleListingContext) as ArticleListingContextProps;
    const parsedServerData = JSON.parse(serverData);
    const [isClient, setIsClient] = useState(false);

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const { search, yearpublished, ...restParams } = getFilterQuery(activeFilters, filters);

    const whereItems: WhereItem[] = [
        {
            type: 'boolean',
            inverse: true,
            field: 'ExcludeFromListing',
            value: ['true'],
        }
    ];
    if (search) {
        whereItems.push({
            type: 'search',
            field: '_fulltext',
            value: search,
        });
    }

    if (yearpublished) {
        whereItems.push({
            type: 'year',
            field: 'ArticlePublishDate',
            value: yearpublished,
        });
    }

    if (filterByAncestors) {
        whereItems.push({
            type: 'ancestors',
            field: 'Ancestors',
            value: filterByAncestors,
        });
    }

    if (restParams) {
        const tagValues = Object.entries(restParams)
            .filter(([key]) => {
                return key.startsWith('tag');
            })
            .map(([, value]) => {
                return value;
            });

        tagValues.forEach(tag => {
            whereItems.push({
                type: 'tag',
                field: 'Tags',
                value: tag,
            });
        });

        // Remove tag parameters from restParams
        Object.keys(restParams).forEach(key => {
            if (key.startsWith('tag')) {
                delete restParams[key];
            }
        });
    }

    const where = getWhereValue(whereItems);

    // We control when this is called through the enabled option
    const queryResult = useArticleListingQuery({
        endpoint: `${cms.apiUrl ? cms.apiUrl : OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`,
        fetchParams: DEFAULT_FETCH_PARAMS,
    }, {
        locale,
        where,
        ...restParams,
        skip: page * itemsPerPage,
        limit: itemsPerPage,
    }, {
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60 * 5,
        initialData: !isClient && parsedServerData?.data ? parsedServerData.data : undefined,
        select: useCallback((result: ArticleListingQuery) => {
            return resolveArticleListing(result, { minReadSuffix: label.minReadSuffix });
        }, [label.minReadSuffix]),
    });

    const data = queryResult?.data;
    const { error = false, isLoading = false, isError = false, isFetching = false } = queryResult || {};

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, filters));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={label.loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    return (
        <div className="listing-app">
            <div className="container">
                {label.title && (
                    <ListingHeader
                        filters={filters}
                        activeFilters={activeFilters}
                        setActiveFilters={updateFilters}
                        title={label.title}
                        clearFilterLabel={label.clearFilterButton}
                    />
                )}
                <ListingContent
                    isFetching={isFetching}
                    isNoItems={!data.items.length}
                    pagination={{
                        itemsPerPage,
                        page,
                        setPage,
                        totalItems: data.total,
                        serverPageUrl: serverPageUrl ?? '',
                    }}
                    loadingLabel={label.loadingMessage}
                    noItemsLabel={label.noItemMessage}
                    cols="3"
                >
                    {data.items.map((props, i) => {
                        return (
                            <CardArticle
                                key={props.title}
                                {...props}
                                size={null}
                                position={(page * itemsPerPage) + i + 1}
                            />
                        );
                    })}
                </ListingContent>
            </div>
        </div>
    );
};


const ArticleListingWithProvider: FC<ArticleListingProps> = props => {
    // eslint-disable-next-line react/hook-use-state
    const [queryClient] = useState(() => {
        return new QueryClient();
    });

    useEffect(() => {
        return () => {
            queryClient.clear(); // Clean up on unmount
        };
    }, [queryClient]);

    return (
        <QueryClientProvider client={queryClient}>
            <ArticleListingProvider {...props}>
                <ArticleListing />
            </ArticleListingProvider>
        </QueryClientProvider>
    );
};

export default ArticleListingWithProvider;
